import React, { useMemo } from 'react';

const UNIT_SYMBOLS = {
  seconds: 's',
  percent: '%',
  count: '',
  ratio: '',
  ms: 'ms',
};

/**
 * Returns CSS color class name based on value vs thresholds (higher-is-better when inverse=false).
 * @param {number} value
 * @param {number|null} target
 * @param {number|null} warning
 * @param {number|null} critical
 * @param {boolean} higherIsBetter
 */
const getThresholdColor = (value, target, warning, critical, higherIsBetter = true) => {
  if (value == null) return '';
  if (higherIsBetter) {
    if (critical != null && value < critical) return 'ccv2-metric-critical';
    if (warning != null && value < warning) return 'ccv2-metric-warning';
    if (target != null && value >= target) return 'ccv2-metric-ok';
    return '';
  } else {
    if (critical != null && value > critical) return 'ccv2-metric-critical';
    if (warning != null && value > warning) return 'ccv2-metric-warning';
    if (target != null && value <= target) return 'ccv2-metric-ok';
    return '';
  }
};

/**
 * Format a metric value for display.
 * @param {number} value
 * @param {string} unit
 */
const formatValue = (value, unit) => {
  if (value == null) return '—';
  if (unit === 'percent') return `${Number(value).toFixed(1)}%`;
  if (unit === 'seconds') {
    const v = Math.round(value);
    if (v < 60) return `${v}s`;
    return `${Math.floor(v / 60)}m ${v % 60}s`;
  }
  if (unit === 'ms') return `${Math.round(value)}ms`;
  return String(Math.round(value * 100) / 100);
};

/**
 * Single KPI metric card.
 * @param {{
 *   label: string,
 *   value: number|null,
 *   unit?: string,
 *   target?: number|null,
 *   warningValue?: number|null,
 *   criticalValue?: number|null,
 *   higherIsBetter?: boolean,
 *   trend?: number|null,
 *   description?: string,
 *   compact?: boolean,
 * }} props
 */
function MetricCard({
  label,
  value,
  unit = '',
  target = null,
  warningValue = null,
  criticalValue = null,
  higherIsBetter = true,
  trend = null,
  description = '',
  compact = false,
}) {
  const colorClass = useMemo(
    () => getThresholdColor(value, target, warningValue, criticalValue, higherIsBetter),
    [value, target, warningValue, criticalValue, higherIsBetter],
  );

  const formattedValue = formatValue(value, unit);

  const trendIcon = trend == null ? null : trend > 0 ? '▲' : trend < 0 ? '▼' : '—';
  const trendClass = trend == null ? '' : trend > 0 ? (higherIsBetter ? 'ccv2-trend-up' : 'ccv2-trend-down') : (higherIsBetter ? 'ccv2-trend-down' : 'ccv2-trend-up');

  return (
    <div className={`ccv2-metric-card ${compact ? 'ccv2-metric-card-compact' : ''} ${colorClass}`} title={description || label}>
      <div className={`ccv2-metric-value ${colorClass}`}>
        {formattedValue}
      </div>
      <div className="ccv2-metric-label">{label}</div>
      {!compact && target != null && (
        <div className="ccv2-metric-target">
          Meta: {formatValue(target, unit)}
        </div>
      )}
      {trendIcon && (
        <span className={`ccv2-metric-trend ${trendClass}`}>{trendIcon}</span>
      )}
    </div>
  );
}

export default React.memo(MetricCard);
